import { useCallback, useEffect, useRef, useState } from "react";

import { debounce } from "../../../utils/debounce";

import type { SortDescriptor } from "@progress/kendo-data-query";
import type { DropDownListPageChangeEvent } from "@progress/kendo-react-dropdowns/DropDownList/DropDownListProps";
import type { FilterChangeEvent } from "@progress/kendo-react-dropdowns/common/events";

interface UseVirtualDataArgs {
  defaultPageSize: number;
  defaultSort: SortDescriptor;
  defaultFilter?: "";
  loadPage: (skip, pageSize, sortField, sortDir, filter) => Promise<any>;
  textField: string;
  dataItemKey: string;
  queryParams?: any[];
}

interface LoadQueryState {
  skip: number;
  pageSize: number;
  sort: SortDescriptor;
  filter: string;
}

interface VirtualDataItem {
  loaded: boolean;
  index: string | number;
}

const createDalaList = <DataItem,>(
  prevData: (DataItem & VirtualDataItem)[],
  loadedData: DataItem[],
  skip: number,
  total: number,
  textField: string,
  dataItemKey: string,
) => {
  let newData;
  if (prevData.length < total) {
    newData = Array.from({ length: total }, (_, ind) => ({
      loaded: ind < prevData.length && prevData[ind]?.loaded ? prevData[ind].loaded : false,
      [textField as keyof DataItem]:
        ind < prevData.length && prevData[ind]?.loaded ?
          prevData[ind][textField as keyof DataItem]
        : "",
      index:
        ind < prevData.length && prevData[ind]?.loaded ?
          `${ind} ${prevData[ind][dataItemKey as keyof DataItem]?.toString()}`
        : ind,
    }));
  } else {
    newData = [...prevData];
  }

  loadedData.forEach((value, i) => {
    newData[i + skip] = {
      ...value,
      loaded: true,
      index: `${i + skip} ${value[dataItemKey as keyof DataItem]?.toString()}`,
    };
  });

  return newData;
};

export const useVirtualData = <DataItem,>({
  loadPage,
  defaultPageSize,
  defaultSort,
  defaultFilter,
  textField,
  dataItemKey,
  queryParams,
}: UseVirtualDataArgs) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<(DataItem & VirtualDataItem)[]>([]);
  const [errors, setErrors] = useState("");
  const [total, setTotal] = useState(0);
  const prevQueryParams = useRef(JSON.stringify(queryParams));
  const isInitialLoad = useRef(true);

  const [queryState, setQueryState] = useState<LoadQueryState>({
    skip: 0,
    pageSize: defaultPageSize,
    sort: defaultSort,
    filter: defaultFilter ?? "",
  });

  const queryStateRef = useRef(queryState);
  queryStateRef.current = queryState;

  const fetchData = useCallback(
    async (state: LoadQueryState) => {
      if (!loadPage) {
        return;
      }

      setLoading(true);

      try {
        const take = state.pageSize * 2;
        const response = (await loadPage(
          state.skip,
          take,
          state.sort.field,
          state.sort.dir,
          state.filter,
        )) as {
          total: number;
          data: DataItem[];
        };

        setData((prevData) =>
          createDalaList<DataItem>(
            prevData,
            response.data,
            state.skip,
            response.total,
            textField,
            dataItemKey,
          ),
        );

        setTotal(response.total);
        setErrors("");
      } catch (error) {
        setErrors(error.toString());
        console.error("Error loading virtual data structure:", error);
      } finally {
        setLoading(false);
      }
    },
    [loadPage, textField, dataItemKey],
  );

  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  const loadQuery = useCallback((state: LoadQueryState) => {
    setData([]);
    setQueryState(state);
    fetchDataRef.current(state);
  }, []);

  const handleFilterChangeRef = useRef(
    debounce((event: FilterChangeEvent) => {
      const nextFilter = (event.filter?.value ?? "") as string;
      const { current } = queryStateRef;

      if (nextFilter === current.filter) {
        return;
      }

      loadQuery({
        ...current,
        skip: 0,
        pageSize: defaultPageSize,
        filter: nextFilter,
      });
    }, 400),
  );

  useEffect(
    () => () => {
      handleFilterChangeRef.current.cancel();
    },
    [],
  );

  // Initial load and refetch when facility type (queryParams) changes
  useEffect(() => {
    if (!loadPage) {
      return;
    }

    const queryParamsStr = JSON.stringify(queryParams);
    const queryParamsChanged = prevQueryParams.current !== queryParamsStr;

    if (!isInitialLoad.current && !queryParamsChanged) {
      return;
    }

    isInitialLoad.current = false;
    prevQueryParams.current = queryParamsStr;

    loadQuery({
      skip: 0,
      pageSize: defaultPageSize,
      sort: defaultSort,
      filter: defaultFilter ?? "",
    });
  }, [loadPage, queryParams, defaultPageSize, defaultSort, defaultFilter, loadQuery]);

  // Virtual scroll pagination
  useEffect(() => {
    if (!data.length) {
      return;
    }

    if (queryState.skip >= data.length && total !== 0) {
      fetchData(queryState);
      return;
    }

    for (
      let i = queryState.skip;
      i < queryState.skip + queryState.pageSize && i < data.length;
      i++
    ) {
      if (!data[i]?.loaded) {
        fetchData(queryState);
        return;
      }
    }
  }, [queryState, data, fetchData, total]);

  const handlePageChange = useCallback((event: DropDownListPageChangeEvent) => {
    setQueryState((prev) => ({
      ...prev,
      skip: event.page.skip,
      pageSize: event.page.take,
    }));
  }, []);

  const handleFilterChange = handleFilterChangeRef.current;

  const handleSortChange = useCallback(
    (event: SortDescriptor) => {
      loadQuery({
        ...queryStateRef.current,
        skip: 0,
        pageSize: defaultPageSize,
        sort: event,
      });
    },
    [defaultPageSize, loadQuery],
  );

  return {
    enabled: !!loadPage,
    errorLoading: errors,
    data,
    loading,
    skip: queryState.skip,
    pageSize: queryState.pageSize,
    total,
    onSortChange: handleSortChange,
    onPageChange: handlePageChange,
    onFilterChange: handleFilterChange,
  };
};
